// Links into Raft (Deck doesn't grow a chat of its own):
// - 「在 Raft 里私聊」 on a person's card (greet.js): opens their page in the Raft web app, whose 「发消息」 (people) /
//   「私信」 (agents) button starts the DM. Raft has no link that opens a DM by person: its DM route takes the DM's own
//   id (/s/<server>/dm/<dm id>), which only Raft knows, so the profile page is the closest one click gets.
// - 会议室 ↔ Raft 频道 (office/raftChannels.ts): the meeting view's header shows 「Raft 频道 #name」 for a bound room;
//   an office admin gets 「绑定 Raft 频道」 / 「改」 there.
// Raft web routes, read from app.raft.build's bundle: /s/<slug>/human/<user id>, /s/<slug>/agent/<agent id>,
// /s/<slug>/channel/<channel id>. A person's Deck sub is "<server id>:<user id>", an agent's "agent:<server id>:<agent id>".
import { icon, put } from '/icons.js';
import { t, onLang, lang, errorText } from '/i18n.js';
export const RAFT_WEB = 'https://app.raft.build';

const CSS = `#meet .mrch{display:inline-flex;align-items:center;gap:6px;min-width:0}
#meet .mrch a,#meet .mrch button{font:inherit;font-size:12px;border:2px solid #3a3f4b;border-radius:999px;padding:4px 10px;background:transparent;color:#FDD03B;cursor:pointer;white-space:nowrap;text-decoration:none;max-width:220px;overflow:hidden;text-overflow:ellipsis}
#meet .mrch button{color:#f4efe4}
@media (max-width:640px){#meet .mrch{flex:0 1 auto;overflow:hidden} #meet .mrch a{max-width:110px} #meet .mrch button .l{display:none}}`;

/** The Raft page of the person or agent `p` (a peer with .sub), or null: no server slug, or they're from another server. */
export function profileUrl(p, server) {
  if (!server?.slug || !server.id || typeof p?.sub !== 'string') return null;
  const s = p.sub.split(':');
  const [kind, sid, id] = s[0] === 'agent' && s.length === 3 ? ['agent', s[1], s[2]] : s.length === 2 ? ['human', s[0], s[1]] : [];
  if (!kind || sid !== server.id || !id) return null;
  return `${RAFT_WEB}/s/${encodeURIComponent(server.slug)}/${kind}/${encodeURIComponent(id)}`;
}
/** A bound room's link: the channel when its id is known, else the server's Raft home. */
export function channelUrl(c, server) {
  if (!server?.slug) return null;
  const s = `${RAFT_WEB}/s/${encodeURIComponent(server.slug)}`;
  return c?.id ? `${s}/channel/${encodeURIComponent(c.id)}` : s;
}

/** @param o {send, toast, server: () => {id, slug}|null} */
export function initRaftLinks(o) {
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  let channels = {}, admin = false, mine = null; // mine: my pending request's id

  const rid = () => Math.random().toString(36).slice(2, 12);
  function bind(room) {
    const cur = channels[room.id];
    const roomName = room.name || t('app.links.room');
    const name = prompt(t(cur ? 'app.links.askBound' : 'app.links.ask', { room: roomName }), cur ? '#' + cur.name : '#');
    if (name === null) return;
    if (!name.trim().replace(/^#/, '')) { if (cur && confirm(t('app.links.unbindAsk', { room: roomName, channel: cur.name }))) { mine = rid(); o.send({ t: 'raft_ch', zone: room.id, clear: true, req: mine }); } return; }
    const link = prompt(t('app.links.askLink'), cur?.id ? channelUrl(cur, o.server()) : '');
    if (link === null) return;
    mine = rid(); o.send({ t: 'raft_ch', zone: room.id, name, link, req: mine });
  }

  const filled = new Map(); // slot -> room: filled again in the new language
  onLang(() => { for (const [slot, room] of filled) { if (slot.isConnected) fill(slot, room); else filled.delete(slot); } });
  /** Fill the meeting header's slot for `room` (a zone {id, name}); cheap to call on every render. */
  function fill(slot, room) {
    filled.set(slot, room);
    const c = room && channels[room.id], url = c && channelUrl(c, o.server());
    const key = JSON.stringify([room?.id, c, url, admin, lang]);
    if (slot.dataset.k === key) return;
    slot.dataset.k = key; slot.replaceChildren();
    if (!room) return;
    if (c) {
      const a = document.createElement('a'); put(a, 'chat', t('app.links.channel', { channel: c.name }));
      a.title = c.id ? t('app.links.open') : t('app.links.openNoLink', { channel: c.name });
      if (url) { a.href = url; a.target = '_blank'; a.rel = 'noopener'; }
      slot.append(a);
    }
    if (admin) {
      const b = document.createElement('button'); b.append(icon('pencil'), Object.assign(document.createElement('span'), { className: 'l', textContent: ' ' + t(c ? 'app.links.edit' : 'app.links.bind') }));
      b.title = t('app.links.bindTip');
      b.onclick = () => bind(room); slot.append(b);
    }
  }

  return {
    welcome(m) { channels = m.roomChannels || {}; },
    /** The bound channels' names (speech to text's hotwords, public/stt.js). */
    names: () => Object.values(channels).map((c) => c.name),
    /** map_permission (office/map-editor.ts): office admins may bind rooms. */
    permission(m) { admin = m.allowed === true; },
    msg(m) {
      if (m.t === 'raft_ch') {
        channels = m.channels || {};
        if (mine && m.req === mine) { const c = channels[m.zone]; o.toast(c ? t('app.links.bound', { channel: c.name }) : t('app.links.unbound')); mine = null; }
      } else if (m.t === 'raft_ch_err') { if (m.req === mine) mine = null; o.toast(m.reason || m.code ? errorText('app.links.err', { code: m.code, message: m.reason }) : t('app.links.failed')); } // app.links.err.<code>
    },
    fill,
    /** For a person's card: their Raft page, or null. */
    profile: (p) => profileUrl(p, o.server()),
  };
}
